import type { BadgeTone } from "../../components/ui/badge";
import type { FeatureKey, FeatureOutcome, FeatureRunStatus, FeatureSetting, FeatureSettings, SentimentLabel } from "./types";
import { FEATURE_KEYS } from "./types";

/** What every null renders as. The service uses null for "not measured", never zero. */
export const DASH = "—";

export const FEATURE_LABELS: Record<FeatureKey, string> = {
  acoustic: "Tone & acoustics",
  emotion: "Emotion",
  sentiment: "Sentiment",
  qa: "Quality (QA)",
  objection: "Objections & compliance",
  cx: "Customer experience",
};

export const isNum = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

export function formatNumber(value: number | null | undefined, digits = 0): string {
  return isNum(value) ? value.toFixed(digits) : DASH;
}

/** Whole seconds as m:ss (or h:mm:ss). Avoids the float artefacts of raw division. */
export function formatClock(seconds: number | null | undefined): string {
  if (!isNum(seconds) || seconds < 0) return DASH;
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
}

/** A -1..+1 score with an explicit sign. Never a percentage: it is an intensity estimate. */
export function formatSigned(value: number | null | undefined, digits = 2): string {
  if (!isNum(value)) return DASH;
  const rounded = Number(value.toFixed(digits));
  if (rounded === 0) return (0).toFixed(digits);
  return `${rounded > 0 ? "+" : "−"}${Math.abs(rounded).toFixed(digits)}`;
}

/** The service's own thresholds (RESULTS.md), for when a label is absent. */
export function sentimentLabelFor(score: number | null | undefined): SentimentLabel | null {
  if (!isNum(score)) return null;
  if (score > 0.2) return "positive";
  if (score < -0.2) return "negative";
  return "neutral";
}

export function sentimentText(score: number | null | undefined, label?: SentimentLabel | null): string {
  const resolved = label ?? sentimentLabelFor(score);
  if (!resolved) return DASH;
  const name = resolved.charAt(0).toUpperCase() + resolved.slice(1);
  return isNum(score) ? `${name} ${formatSigned(score)}` : name;
}

export function sentimentTone(label: SentimentLabel | null | undefined): BadgeTone {
  if (label === "positive") return "success";
  if (label === "negative") return "danger";
  return "neutral";
}

/** A 0..1 rate as a percentage. */
export function formatRate(rate: number | null | undefined): string {
  return isNum(rate) ? `${Math.round(rate * 100)}%` : DASH;
}

export function levelTone(level: string | null | undefined): BadgeTone {
  if (level === "high") return "danger";
  if (level === "medium") return "warning";
  if (level === "low") return "success";
  return "neutral";
}

export function humanize(value: string | null | undefined): string {
  if (!value) return DASH;
  const text = value.replace(/[_:]+/g, " ").trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Highest counts first; ties keep the server's order. */
export function topEntries(record: Record<string, number> | null | undefined, limit = 5): Array<[string, number]> {
  return Object.entries(record ?? {})
    .filter(([, count]) => isNum(count) && count > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit);
}

// ── Feature run state ───────────────────────────────────────────────────

export interface RunState {
  kind: "not_run" | FeatureRunStatus;
  text: string;
  tone: BadgeTone;
}

export function runState(outcome: Pick<FeatureOutcome<unknown>, "status" | "reason"> | null | undefined): RunState {
  if (!outcome) return { kind: "not_run", text: "Not enabled for this call", tone: "neutral" };
  const reason = outcome.reason ? `: ${outcome.reason}` : "";
  if (outcome.status === "completed") return { kind: "completed", text: "Completed", tone: "success" };
  if (outcome.status === "skipped") return { kind: "skipped", text: `Skipped${reason}`, tone: "warning" };
  return { kind: "failed", text: `Failed${reason}`, tone: "danger" };
}

// ── Configuration ───────────────────────────────────────────────────────

/** `enabled` is tri-state at organization and agent level: null means "not set here". */
export type TriState = "on" | "off" | "unset";

export function toTriState(enabled: boolean | null | undefined): TriState {
  if (enabled === true) return "on";
  if (enabled === false) return "off";
  return "unset";
}

export function fromTriState(state: TriState): boolean | null {
  return state === "on" ? true : state === "off" ? false : null;
}

export const TRI_STATE_LABELS: Record<"organization" | "agent", Record<TriState, string>> = {
  organization: { on: "On", off: "Off (restricts agents)", unset: "Not set (agents may opt in)" },
  agent: { on: "On (opt in)", off: "Off (opt out)", unset: "Inherit" },
};

/** `decided_by` in words. `enabled` is the effective outcome it explains. */
export function decidedByText(decidedBy: string, enabled: boolean): string {
  if (decidedBy === "master_off") return "Off because Call Analytics is not enabled for your organization by TechLadder.";
  if (decidedBy === "default") return enabled ? "On by default." : "Off: no level has turned it on.";
  // The service only ever names platform for "not available": platform true means available, not on.
  if (decidedBy === "platform") return "Off: not available on your plan (set by TechLadder).";
  if (decidedBy === "organization") {
    return enabled ? "On for every agent by your organization's setting." : "Off: your organization's setting restricts it.";
  }
  if (decidedBy === "agent") return enabled ? "On: this agent opted in." : "Off: this agent opted out.";
  return `${enabled ? "On" : "Off"}, decided by ${humanize(decidedBy)}.`;
}

/** Only labels the Save button: the PUT still sends the whole draft, as it replaces the map. */
export function countChanges(original: FeatureSettings, draft: FeatureSettings): number {
  const same = (a: FeatureSetting | undefined, b: FeatureSetting | undefined) =>
    (a?.enabled ?? null) === (b?.enabled ?? null) && (a?.sample_pct ?? null) === (b?.sample_pct ?? null);
  return FEATURE_KEYS.filter((key) => !same(original[key], draft[key])).length;
}

/** "" clears the value (inherit); anything else must be a 0..100 number. */
export function parseSamplePct(text: string): number | null | "invalid" {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  return Number.isFinite(value) && value >= 0 && value <= 100 ? value : "invalid";
}

// ── Prompt diff ─────────────────────────────────────────────────────────

export interface DiffLine {
  kind: "same" | "removed" | "added";
  text: string;
}

/** Line diff by longest common subsequence; prompts are small enough for O(n·m). */
export function lineDiff(before: string, after: string): DiffLine[] {
  const a = before ? before.split("\n") : [];
  const b = after ? after.split("\n") : [];
  const lcs: number[][] = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      out.push({ kind: "same", text: a[i] });
      i += 1;
      j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      out.push({ kind: "removed", text: a[i] });
      i += 1;
    } else {
      out.push({ kind: "added", text: b[j] });
      j += 1;
    }
  }
  while (i < a.length) out.push({ kind: "removed", text: a[i++] });
  while (j < b.length) out.push({ kind: "added", text: b[j++] });
  return out;
}

// ── Dates ───────────────────────────────────────────────────────────────

/** Inclusive local calendar days to the service's [from, to) instants. */
export function rangeToParams(start: Date, end: Date): { from: string; to: string } {
  const from = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const to = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1);
  return { from: from.toISOString(), to: to.toISOString() };
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return DASH;
  return date.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}
